import {createReducer, on} from '@ngrx/store';
import {getDataLoaderReducerFn, LoadingStatus, nameOfFactory, status} from 'amc-common-components';
import {assoc, assocPath} from 'ramda';
import {ItemCommon} from '@entities/common/common.interface';
import {
	CtsEditPeriodData,
	CtsSavedRows,
	CtsSorting,
	CtsTableDataWithPagination,
	CtsTableFilters,
} from '@entities/cts-distribution/cts-distribution.interfaces';
import {Moment} from 'moment/moment';
import {ctsDistributionActions} from './actions';

/**
 * Auxiliary store state interface declaration.
 */
export interface CtsDistributionState {
	/** {CtsTableDataWithPagination} table */
	table: CtsTableDataWithPagination;
	/** {LoadingStatus} tableLoadingStatus */
	tableLoadingStatus: LoadingStatus;
	/** {ItemCommon<string>[]} platforms */
	platforms: ItemCommon[];
	/** {LoadingStatus} platformsLoadingStatus */
	platformsLoadingStatus: LoadingStatus;
	/** {CtsEditPeriodData} periodFilter */
	periodFilter: CtsEditPeriodData;
	/** {LoadingStatus} periodFilterLoadingStatus */
	periodFilterLoadingStatus: LoadingStatus;
	/** {ItemCommon[]} programTypeFilter */
	programTypeFilter: ItemCommon[];
	/** {LoadingStatus} programTypeFilterLoadingStatus */
	programTypeFilterLoadingStatus: LoadingStatus;
	/** {CtsSavedRows} savedRows */
	savedRows: CtsSavedRows;
	/** {LoadingStatus} savedRowsLoadingStatus */
	savedRowsLoadingStatus: LoadingStatus;
	/** {number} pagesCount */
	pagesCount: number;
	/** {LoadingStatus} pagesCountLoadingStatus */
	pagesCountLoadingStatus: LoadingStatus;
	/** {number} currentPage */
	currentPage: number;
	/** {CtsTableFilters} tableFilters */
	tableFilters: CtsTableFilters;
	/** {Moment} historicalDate */
	historicalDate: Moment;
	/** {CtsSorting} tableSorting */
	tableSorting: CtsSorting;
}

/** Auxiliary 'initialState' variable. */
const initialState: CtsDistributionState = {
	table: {
		data: null,
		pagination: null,
	},
	tableLoadingStatus: status.loading,
	platforms: [],
	platformsLoadingStatus: status.loading,
	periodFilter: null,
	periodFilterLoadingStatus: status.loading,
	programTypeFilter: [],
	programTypeFilterLoadingStatus: status.loading,
	savedRows: null,
	savedRowsLoadingStatus: status.default,
	pagesCount: 1,
	pagesCountLoadingStatus: status.loading,
	currentPage: 1,
	tableFilters: null,
	historicalDate: null,
	tableSorting: {
		column: null,
		order: null,
	},
};

/** Auxiliary 'prop' variable.' */
const prop = nameOfFactory<CtsDistributionState>();

/** Auxiliary 'ctsDistributionStateReducer' variable. */
export const ctsDistributionStateReducer = createReducer(
	initialState,
	...getDataLoaderReducerFn()(ctsDistributionActions.getTableData, prop('table')),
	...getDataLoaderReducerFn()(ctsDistributionActions.getPlatformFilter, prop('platforms')),
	...getDataLoaderReducerFn()(ctsDistributionActions.getPeriodFilter, prop('periodFilter')),
	...getDataLoaderReducerFn()(ctsDistributionActions.getProgramTypeFilter, prop('programTypeFilter')),
	...getDataLoaderReducerFn()(ctsDistributionActions.saveRows, prop('savedRows')),
	...getDataLoaderReducerFn()(ctsDistributionActions.getPagesCount, prop('pagesCount')),
	on(ctsDistributionActions.selectPage, (state, {payload}) => assoc(prop('currentPage'), payload, state)),
	on(ctsDistributionActions.updateTableRows, (state, {payload}) => assocPath(['table', 'data'], payload, state)),
	on(ctsDistributionActions.setTableFilters, (state, {payload}) => assoc(prop('tableFilters'), payload, state)),
	on(ctsDistributionActions.setTableSorting, (state, {payload}) => assoc(prop('tableSorting'), payload, state)),
	on(ctsDistributionActions.setHistoricalDate, (state, {payload}) => assoc(prop('historicalDate'), payload, state)),
	on(ctsDistributionActions.resetState, () => JSON.parse(JSON.stringify(initialState)))
);
